/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosError } from "axios";
import Button from "components/Button";
import { SharedFlowState } from "components/Flow/types";
import Form from "components/Form";
import Label from "components/Label";
import useAsync from "hooks/use-async";
import { useSession } from "hooks/use-session";
import segment from "lib/segment";
import React, { FC } from "react";
import toast from "react-hot-toast";
import { useRecoilState } from "recoil";
import {
  formatErrors,
  formatHSErrors,
  HSPayload,
  parseAdrAddress,
  Place,
  required,
  validEmail
} from "utils/form";
import { purgeObject } from "utils/object";

import styles from "./styles.module.scss";

type Props = {
  sharedFlowState: SharedFlowState;
  nextStep(): void;
};

interface FormValues {
  email: string;
  firstname: string;
  lastname: string;
  phone: string;
}

interface FinalValues extends FormValues {
  address: Place;
  interests: string[];
}

const UserInfo: FC<Props> = ({ sharedFlowState, nextStep }) => {
  const [sharedState, setSharedState] = useRecoilState(sharedFlowState);

  const { utm } = useSession();

  const [submit, submitting] = useAsync(async (values: FinalValues) => {
    const newValues = formatPayload(values);
    segment.track("LeadFlowSubmitted", newValues);

    await axios
      .post(
        `https://api.hsforms.com/submissions/v3/integration/submit/20608861/7c39c5b7-e021-427d-a2c5-0f977689e826`,
        {
          fields: Object.keys(newValues).map((key) => ({
            name: key,
            value: newValues[key as keyof FormValues]
          }))
        }
      )
      .then(() => {
        toast.success("We've saved your information!", {
          position: "top-right"
        });

        nextStep();
      })
      .catch((e) => {
        const payload = (e as AxiosError).response?.data;
        toast.error(
          payload
            ? formatHSErrors(payload as HSPayload)
            : "Something went wrong"
        );
      });
  });

  const formatPayload = (values: FormValues) => {
    const newValues: { [key: string]: any } = { ...values };

    const { firstname, lastname, phone, email } = values;
    const { address: place } = newValues;

    delete newValues.address;

    const { state, zip, city, country, address } = parseAdrAddress(
      place.adr_address
    );

    return {
      "0-2/country": country,
      "0-2/address": `${address} ${city} ${state} ${zip}`,
      "0-2/name": `${address} ${city} ${state} ${zip}`,
      "0-2/zip": zip,
      "0-2/city": city,
      "0-2/state": state,
      firstname,
      lastname,
      phone,
      email,
      form_variant: "lead_flow",
      ...purgeObject(utm)
    } as { [key: string]: string };
  };

  return (
    <div className={styles.container}>
      <Label className={styles.title} variant="centered-h4" color="primary2">
        Let&apos;s save your information before we get ahead of ourselves.
      </Label>
      <Label className={styles.subtitle} variant="centered-body" color="white">
        This helps you access your plan later on.
      </Label>
      <Form
        id="lead_flow"
        initialValues={sharedState}
        className={styles.form}
        onSubmit={(values: FormValues) => {
          setSharedState({ ...sharedState, ...values });
          submit({ ...sharedState, ...values });
        }}
        onError={(errors) =>
          toast.error(formatErrors(errors), { position: "top-right" })
        }
        fields={[
          {
            type: "text",
            id: "firstname",
            validate: [required("Please enter your first name")],
            placeholder: "First name"
          },
          {
            type: "text",
            id: "lastname",
            validate: [required("Please enter your last name")],
            placeholder: "Last name"
          },
          {
            type: "text",
            id: "email",
            placeholder: "Email address",
            validate: [
              required("Please enter your email address"),
              validEmail("Invalid email")
            ]
          },
          {
            type: "text",
            id: "phone",
            placeholder: "Phone number",
            validate: [required("Please enter your phone number")]
          }
        ]}
      >
        {({ invalid }) => (
          <Button
            type="submit"
            loading={submitting}
            className={styles.submit}
            disabled={invalid}
            variant="accent"
          >
            Continue
          </Button>
        )}
      </Form>
      <Label
        className={styles.terms}
        variant="centered-xsmall"
        color="primary2"
      >
        By clicking Continue, I agree to the Humming Homes of Use and Privacy
        Policy, and that Humming Homes and its representatives may call or text
        me at the phone number entered above, including using an autodialer,
        regarding my renovation and to give me estimates, offers and other
        information about its products and services. I understand that my
        consent is not required as a condition of any purchase.
      </Label>
    </div>
  );
};

export default UserInfo;
